import { base58Decode, bytesToHex, keccak256Bytes, sha256Bytes, utf8Bytes } from './hash.js';

/**
 * USDT payouts: the networks, and what a wallet address on each has to look
 * like before Nexus will seal and store it.
 *
 * THE WRONG NETWORK LOSES THE MONEY. USDT exists on several chains and a
 * transfer sent on one to an address from another is gone, with nobody to ask
 * for it back. So the network is a first-class choice the creator makes, the
 * address is checked against THAT network's format and checksum where one
 * exists, and every sentence that refuses an address names the network it was
 * checked against. A creator who pastes an Ethereum address under "TRON" is
 * told so, not told "invalid".
 *
 * Pure and synchronous: this runs in the browser as the creator types, and in
 * the Worker again before the value is sealed. No I/O, no zod (the request
 * schema in `contracts.ts` calls `validateWalletAddress`).
 */

export const CRYPTO_ASSETS = ['USDT'] as const;
export type CryptoAsset = (typeof CRYPTO_ASSETS)[number];

/**
 * The networks a USDT payout can be sent on. Pinned in the database by the
 * `CreatorPayoutProfile_cryptoNetwork_enum_*` triggers (migration 0013), so
 * adding one is a trigger change and a line here, never a rebuild.
 */
export const CRYPTO_NETWORKS = [
  'TRON_TRC20',
  'ETHEREUM_ERC20',
  'POLYGON',
  'BSC_BEP20',
  'SOLANA',
  'ARBITRUM',
] as const;
export type CryptoNetwork = (typeof CRYPTO_NETWORKS)[number];

/** How each network's addresses are shaped. */
export type AddressFamily = 'tron' | 'evm' | 'solana';

export interface CryptoNetworkMeta {
  network: CryptoNetwork;
  /** What a person reads: the chain, and the token standard in brackets. */
  label: string;
  family: AddressFamily;
  /** Where a transaction hash can be looked up. `{tx}` is replaced. */
  explorerTx: string;
  /** Where an address can be looked up. `{address}` is replaced. */
  explorerAddress: string;
}

export const CRYPTO_NETWORK_META: Readonly<Record<CryptoNetwork, CryptoNetworkMeta>> = Object.freeze({
  TRON_TRC20: {
    network: 'TRON_TRC20',
    label: 'TRON (TRC-20)',
    family: 'tron',
    explorerTx: 'https://tronscan.org/#/transaction/{tx}',
    explorerAddress: 'https://tronscan.org/#/address/{address}',
  },
  ETHEREUM_ERC20: {
    network: 'ETHEREUM_ERC20',
    label: 'Ethereum (ERC-20)',
    family: 'evm',
    explorerTx: 'https://etherscan.io/tx/{tx}',
    explorerAddress: 'https://etherscan.io/address/{address}',
  },
  POLYGON: {
    network: 'POLYGON',
    label: 'Polygon',
    family: 'evm',
    explorerTx: 'https://polygonscan.com/tx/{tx}',
    explorerAddress: 'https://polygonscan.com/address/{address}',
  },
  BSC_BEP20: {
    network: 'BSC_BEP20',
    label: 'BNB Smart Chain (BEP-20)',
    family: 'evm',
    explorerTx: 'https://bscscan.com/tx/{tx}',
    explorerAddress: 'https://bscscan.com/address/{address}',
  },
  SOLANA: {
    network: 'SOLANA',
    label: 'Solana',
    family: 'solana',
    explorerTx: 'https://solscan.io/tx/{tx}',
    explorerAddress: 'https://solscan.io/account/{address}',
  },
  ARBITRUM: {
    network: 'ARBITRUM',
    label: 'Arbitrum One',
    family: 'evm',
    explorerTx: 'https://arbiscan.io/tx/{tx}',
    explorerAddress: 'https://arbiscan.io/address/{address}',
  },
});

export const CRYPTO_NETWORK_LABELS: Readonly<Record<CryptoNetwork, string>> = Object.freeze(
  Object.fromEntries(CRYPTO_NETWORKS.map((n) => [n, CRYPTO_NETWORK_META[n].label])) as Record<
    CryptoNetwork,
    string
  >,
);

export function isCryptoNetwork(value: unknown): value is CryptoNetwork {
  return typeof value === 'string' && (CRYPTO_NETWORKS as readonly string[]).includes(value);
}

/** The one-line warning shown beside the network select. */
export function networkWarningSentence(network: CryptoNetwork): string {
  return `Send only USDT on ${CRYPTO_NETWORK_META[network].label}; the wrong network loses the money.`;
}

/* ------------------------------------------------------------------ */
/* Validation                                                          */
/* ------------------------------------------------------------------ */

export type AddressVerdict =
  | {
      ok: true;
      /** The address as it will be stored: EIP-55 checksummed for EVM chains, as typed otherwise. */
      normalized: string;
      last6: string;
      family: AddressFamily;
    }
  | { ok: false; message: string };

const EVM_HEX = /^0x[0-9a-fA-F]{40}$/;

/**
 * EIP-55: the checksum IS the casing. Each hex letter is upper-case when the
 * corresponding nibble of `keccak256(lowercase address without 0x)` is 8 or
 * above. An all-lower-case or all-upper-case address carries no checksum and
 * is accepted as typed (that is how many wallets print them); a MIXED-case
 * address that does not match is a typo somewhere in the middle of forty
 * characters and is refused, because the whole point of the checksum is that
 * a one-character slip lands on nobody's wallet rather than on a stranger's.
 */
export function toEip55(address: string): string {
  const lower = address.slice(2).toLowerCase();
  const hash = bytesToHex(keccak256Bytes(utf8Bytes(lower)));
  let out = '0x';
  for (let i = 0; i < lower.length; i += 1) {
    const char = lower[i]!;
    const nibble = parseInt(hash[i]!, 16);
    out += /[a-f]/.test(char) && nibble >= 8 ? char.toUpperCase() : char;
  }
  return out;
}

function validateEvm(address: string, label: string): AddressVerdict {
  if (!EVM_HEX.test(address)) {
    if (/^0x/i.test(address)) {
      return {
        ok: false,
        message: `A ${label} address is 0x followed by exactly 40 hex characters; this one is not.`,
      };
    }
    return {
      ok: false,
      message: `That is not a ${label} address. One starts with 0x and is 42 characters long.`,
    };
  }
  const body = address.slice(2);
  const hasLower = /[a-f]/.test(body);
  const hasUpper = /[A-F]/.test(body);
  const checksummed = toEip55(address);
  if (hasLower && hasUpper && checksummed !== address) {
    return {
      ok: false,
      message: `That ${label} address fails its checksum: one of the characters is wrong. Copy it again from your wallet.`,
    };
  }
  return { ok: true, normalized: checksummed, last6: checksummed.slice(-6), family: 'evm' };
}

function validateTron(address: string, label: string): AddressVerdict {
  if (!address.startsWith('T') || address.length !== 34) {
    return {
      ok: false,
      message: `A ${label} address starts with T and is 34 characters long; this one is not.`,
    };
  }
  const decoded = base58Decode(address);
  if (!decoded) {
    return {
      ok: false,
      message: `That ${label} address contains a character TRON addresses never use (0, O, I or l).`,
    };
  }
  if (decoded.length !== 25 || decoded[0] !== 0x41) {
    return { ok: false, message: `That is not a ${label} address.` };
  }
  const payload = decoded.slice(0, 21);
  const expected = sha256Bytes(sha256Bytes(payload)).slice(0, 4);
  const actual = decoded.slice(21);
  for (let i = 0; i < 4; i += 1) {
    if (expected[i] !== actual[i]) {
      return {
        ok: false,
        message: `That ${label} address fails its checksum: one of the characters is wrong. Copy it again from your wallet.`,
      };
    }
  }
  return { ok: true, normalized: address, last6: address.slice(-6), family: 'tron' };
}

function validateSolana(address: string, label: string): AddressVerdict {
  if (/^0x/i.test(address)) {
    return {
      ok: false,
      message: `That looks like an Ethereum-style address, not a ${label} one. A Solana address is Base58 with no 0x.`,
    };
  }
  if (address.length < 32 || address.length > 44) {
    return {
      ok: false,
      message: `A ${label} address is between 32 and 44 characters; this one is ${address.length}.`,
    };
  }
  const decoded = base58Decode(address);
  if (!decoded) {
    return {
      ok: false,
      message: `That ${label} address contains a character Solana addresses never use (0, O, I or l).`,
    };
  }
  if (decoded.length !== 32) {
    return { ok: false, message: `That is not a ${label} address: it does not decode to a 32-byte key.` };
  }
  return { ok: true, normalized: address, last6: address.slice(-6), family: 'solana' };
}

/**
 * The characters a chat app or a web page pastes INTO an address without
 * anybody seeing them: zero-width space / non-joiner / joiner (U+200B–D), the
 * word joiner (U+2060) and the byte-order mark (U+FEFF). None is whitespace to
 * `trim()` or to `\s`, so an address carrying one used to be refused as
 * "not 40 hex characters" while looking exactly right on the screen. They are
 * removed, never stored: what is sealed is the address, not its invisible
 * decoration. Soft hyphens (U+00AD) likewise.
 */
const INVISIBLE = /[\u200B-\u200D\u2060\uFEFF\u00AD]/g;
const stripInvisible = (text: string): string => text.replace(INVISIBLE, '');

/**
 * Is this a wallet address on this network?
 *
 * Trims whitespace (a copied address often carries a trailing newline),
 * drops the invisible characters above, and refuses anything else with a
 * sentence that names the network. Never "invalid address" on its own.
 */
export function validateWalletAddress(network: CryptoNetwork, raw: string): AddressVerdict {
  const address = stripInvisible(raw).trim();
  const meta = CRYPTO_NETWORK_META[network];
  if (address.length === 0) {
    return { ok: false, message: `Paste your ${meta.label} wallet address.` };
  }
  if (/\s/.test(address)) {
    return { ok: false, message: `A ${meta.label} address has no spaces in it.` };
  }
  switch (meta.family) {
    case 'evm':
      return validateEvm(address, meta.label);
    case 'tron':
      return validateTron(address, meta.label);
    case 'solana':
      return validateSolana(address, meta.label);
  }
}

/* ------------------------------------------------------------------ */
/* Transaction hashes                                                  */
/* ------------------------------------------------------------------ */

/**
 * Does this reference look like a transaction hash on this network?
 *
 * The operator records the on-chain hash as the payout's reference — it is
 * the only thing that ties a USDT transfer to a statement — so a reference
 * that is plainly not one ("sent it", "PAYPAL-4412") is refused with a
 * sentence rather than stored. Shape only: nothing here reads the chain.
 */
export function validateTxHash(network: CryptoNetwork, raw: string): { ok: true; hash: string } | { ok: false; message: string } {
  const hash = raw.trim();
  const meta = CRYPTO_NETWORK_META[network];
  switch (meta.family) {
    case 'evm':
      if (/^0x[0-9a-fA-F]{64}$/.test(hash)) return { ok: true, hash: hash.toLowerCase() };
      return {
        ok: false,
        message: `A ${meta.label} transaction hash is 0x followed by 64 hex characters. Paste the hash from the explorer, not a note.`,
      };
    case 'tron':
      if (/^[0-9a-fA-F]{64}$/.test(hash)) return { ok: true, hash: hash.toLowerCase() };
      return {
        ok: false,
        message: `A ${meta.label} transaction hash is 64 hex characters with no 0x. Paste the hash from Tronscan.`,
      };
    case 'solana': {
      const decoded = hash.length >= 80 && hash.length <= 90 ? base58Decode(hash) : null;
      if (decoded && decoded.length === 64) return { ok: true, hash };
      return {
        ok: false,
        message: `A ${meta.label} transaction signature is a Base58 string of about 88 characters. Paste it from Solscan.`,
      };
    }
  }
}

export function explorerTxUrl(network: CryptoNetwork, hash: string): string {
  return CRYPTO_NETWORK_META[network].explorerTx.replace('{tx}', encodeURIComponent(hash));
}

export function explorerAddressUrl(network: CryptoNetwork, address: string): string {
  return CRYPTO_NETWORK_META[network].explorerAddress.replace('{address}', encodeURIComponent(address));
}

/** `…` plus the last six characters: what every screen shows instead of the address. */
export function maskWalletAddress(last6: string): string {
  return `…${last6}`;
}

/** `0x5aAe…1BeAed` for a screen that wants a little more than the tail. */
export function shortHash(hash: string): string {
  if (hash.length <= 14) return hash;
  return `${hash.slice(0, 8)}…${hash.slice(-6)}`;
}

/* ------------------------------------------------------------------ */
/* The fee note                                                        */
/* ------------------------------------------------------------------ */

export const USDT_FEE_POLICIES = ['creator', 'weclip'] as const;
export type UsdtFeePolicy = (typeof USDT_FEE_POLICIES)[number];

/**
 * Who pays the network fee, from the `USDT_FEE_POLICY` var.
 *
 * DEFAULT `creator`, and anything unrecognised is `creator` too: the operator
 * sends the gross amount either way (nothing here deducts), so the policy only
 * changes the sentence on the instructions page and the wallet — and the
 * safe wrong sentence is the one that does not promise WeClip will cover
 * something.
 */
export function usdtFeePolicy(raw: string | undefined | null): UsdtFeePolicy {
  const value = (raw ?? '').trim().toLowerCase();
  return value === 'weclip' ? 'weclip' : 'creator';
}

export function usdtFeeSentence(policy: UsdtFeePolicy): string {
  return policy === 'weclip'
    ? 'Network fee: paid by WeClip. Send the gross amount and cover the transfer fee from the sending wallet.'
    : 'Network fee: paid by the creator. Send the gross amount; any fee to move the USDT on afterwards is theirs.';
}
